<template>
  <section class="scenarios-page" :dir="pageDirection">
    <!-- Page header -->
    <header class="page-header">
      <div class="page-header-content">
        <h1>{{ tr("pageTitle") }}</h1>

        <p>{{ tr("pageDescription") }}</p>

        <span class="reference-badge">
          {{ tr("reference") }}
        </span>
      </div>
    </header>

    <!-- Summary cards -->
    <div class="summary-grid">
      <article
        v-for="item in summaryItems"
        :key="item.key"
        class="summary-card"
        :class="item.className"
      >
        <strong>{{ item.value }}</strong>

        <span>{{ item.label }}</span>
      </article>
    </div>

    <!-- Initial loading -->
    <div
      v-if="initialLoading"
      class="state-box loading-box"
      role="status"
      aria-live="polite"
    >
      <div class="loading-spinner"></div>

      <strong>{{ tr("states.loadingTitle") }}</strong>

      <span>{{ tr("states.loadingDescription") }}</span>
    </div>

    <!-- Error -->
    <div
      v-else-if="errorMessage && scenarios.length === 0"
      class="state-box error-box"
      role="alert"
    >
      <div class="state-icon">!</div>

      <strong>{{ tr("states.errorTitle") }}</strong>

      <p>{{ errorMessage }}</p>

      <button
        type="button"
        class="retry-button"
        @click="fetchDecisionScenarios"
      >
        {{ tr("actions.retry") }}
      </button>
    </div>

    <!-- Empty state -->
    <div v-else-if="scenarios.length === 0" class="state-box empty-box">
      <div class="state-icon">i</div>

      <strong>{{ tr("states.emptyTitle") }}</strong>

      <p>{{ tr("states.emptyDescription") }}</p>
    </div>

    <!-- Scenarios -->
    <div v-else class="scenarios-list" :class="{ 'is-refreshing': refreshing }">
      <article
        v-for="scenario in scenarios"
        :key="scenario.code"
        class="scenario-card"
      >
        <!-- Scenario head -->
        <div class="scenario-head">
          <div class="scenario-title-group">
            <div class="scenario-number">
              <!-- {{ scenario.code }} -->
              <span v-if="scenario.name" class="scenario-name">
                {{ scenario.name }}
              </span>
            </div>

            <h2>
              {{ getScenarioTitle(scenario) }}
            </h2>

            <div class="scenario-meta">
              <span>
                {{ tr("labels.facilitiesCount") }}:

                <strong>
                  {{ scenario.facilities_count }}
                </strong>
              </span>

              <span>
                {{ tr("labels.urgentCases") }}:

                <strong>
                  {{ scenario.urgent_facilities_count }}
                </strong>
              </span>
            </div>
          </div>

          <span class="severity" :class="scenario.severity">
            {{ getSeverityLabel(scenario) }}
          </span>
        </div>

        <!-- Trigger -->
        <div class="trigger-box">
          <span>
            {{ tr("activationCriteria") }}
          </span>

          <strong>
            {{ getScenarioTrigger(scenario) }}
          </strong>
        </div>

        <!-- Facilities -->
        <div class="facilities-box">
          <h3>
            {{ tr("triggeredFacilities") }}

            <span class="facilities-count">
              ({{ scenario.facilities_count }})
            </span>
          </h3>

          <div v-if="scenario.facilities?.length" class="facilities-list">
            <article
              v-for="facility in scenario.facilities"
              :key="`${scenario.code}-${facility.survey_id}`"
              class="facility-card"
              :class="{
                urgent: facility.needs_immediate,
              }"
            >
              <!-- Facility head -->
              <div class="facility-head">
                <div class="facility-title">
                  <h4>{{ facility.name }}</h4>

                  <p>
                    <span v-if="facility.hf_code">
                      {{ facility.hf_code }}
                    </span>

                    <span v-if="facility.district">
                      {{ facility.district }}
                    </span>

                    <span v-if="facility.governorate">
                      {{ facility.governorate }}
                    </span>
                  </p>
                </div>

                <span
                  class="facility-priority"
                  :class="getPriorityClass(facility.priority)"
                >
                  {{ getPriorityLabel(facility.priority) }}
                </span>
              </div>

              <!-- Facility basic info -->
              <div class="facility-info-grid">
                <div class="facility-info">
                  <span>
                    {{ tr("labels.timeframe") }}
                  </span>

                  <strong>
                    {{ facility.timeframe || "-" }}
                  </strong>
                </div>

                <div class="facility-info">
                  <span>
                    {{ tr("labels.interventionType") }}
                  </span>

                  <strong>
                    {{
                      facility.needs_immediate
                        ? tr("values.immediateIntervention")
                        : tr("values.plannedIntervention")
                    }}
                  </strong>
                </div>
              </div>

              <!-- Activation reason -->
              <div v-if="facility.reason" class="facility-reason">
                <span>
                  {{ tr("labels.activationReason") }}
                </span>

                <p>{{ facility.reason }}</p>
              </div>

              <!-- Phase reason -->
              <div v-if="facility.phase_reason" class="facility-phase-reason">
                <span>
                  {{ tr("labels.phaseReason") }}
                </span>

                <p>{{ facility.phase_reason }}</p>
              </div>

              <!-- Facility details -->
              <details v-if="facility.details" class="facility-details">
                <summary>
                  {{ tr("actions.showDetails") }}
                </summary>

                <!-- SCN-01 -->
                <div v-if="scenario.code === 'SCN-01'" class="details-content">
                  <div class="details-grid">
                    <div>
                      <span>
                        {{ tr("details.affectedBuildings") }}
                      </span>

                      <strong>
                        {{ facility.details.affected_buildings ?? 0 }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.urgentBuildings") }}
                      </span>

                      <strong>
                        {{ facility.details.urgent_buildings ?? 0 }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.highestDamageIndex") }}
                      </span>

                      <strong>
                        {{ formatNumber(facility.details.worst_bdi) }}
                      </strong>
                    </div>
                  </div>

                  <div
                    v-if="facility.details.buildings?.length"
                    class="details-table-wrapper"
                  >
                    <table class="details-table">
                      <thead>
                        <tr>
                          <th>
                            {{ tr("details.building") }}
                          </th>

                          <th>BDI</th>

                          <th>
                            {{ tr("details.priority") }}
                          </th>

                          <th>
                            {{ tr("details.status") }}
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        <tr
                          v-for="building in facility.details.buildings"
                          :key="building.building_id"
                        >
                          <td>
                            {{ building.name || "-" }}
                          </td>

                          <td>
                            {{ formatNumber(building.bdi) }}
                          </td>

                          <td>
                            {{ getPriorityLabel(building.priority) }}
                          </td>

                          <td>
                            {{
                              building.needs_immediate
                                ? tr("values.urgent")
                                : tr("values.planned")
                            }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- SCN-03 -->
                <div
                  v-else-if="scenario.code === 'SCN-03'"
                  class="details-content"
                >
                  <div class="details-grid">
                    <div>
                      <span>
                        {{ tr("details.electricityHours") }}
                      </span>

                      <strong>
                        {{ formatHours(facility.details.electricity_hours) }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.generators") }}
                      </span>

                      <strong>
                        {{ facility.details.generator_count ?? 0 }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.coldChain") }}
                      </span>

                      <strong>
                        {{
                          translateColdChain(facility.details.cold_chain_power)
                        }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.oxygenStatus") }}
                      </span>

                      <strong>
                        {{
                          facility.details.oxygen_failed
                            ? tr("values.notWorking")
                            : tr("values.available")
                        }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.electricitySource") }}
                      </span>

                      <strong>
                        {{ facility.details.main_source || "-" }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.fuelAvailability") }}
                      </span>

                      <strong>
                        {{ facility.details.fuel_availability || "-" }}
                      </strong>
                    </div>
                  </div>
                </div>

                <!-- SCN-04 -->
                <div
                  v-else-if="scenario.code === 'SCN-04'"
                  class="details-content"
                >
                  <div class="details-grid">
                    <div>
                      <span>HWAI</span>

                      <strong>
                        {{ formatPercentage(facility.details.hwai) }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.occupancyRate") }}
                      </span>

                      <strong>
                        {{ formatPercentage(facility.details.occupancy_pct) }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.authorizedPosts") }}
                      </span>

                      <strong>
                        {{ facility.details.authorized_posts ?? 0 }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.filledPosts") }}
                      </span>

                      <strong>
                        {{ facility.details.filled_posts ?? 0 }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.missingCriticalSpecialties") }}
                      </span>

                      <strong>
                        {{ facility.details.missing_critical_count ?? 0 }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.absenceLast30Days") }}
                      </span>

                      <strong>
                        {{ facility.details.absent_staff ?? 0 }}
                      </strong>
                    </div>
                  </div>

                  <div
                    v-if="facility.details.missing_cadres"
                    class="details-note"
                  >
                    <span>
                      {{ tr("details.missingSpecialties") }}
                    </span>

                    <p>
                      {{ facility.details.missing_cadres }}
                    </p>
                  </div>
                </div>

                <!-- SCN-05 -->
                <div
                  v-else-if="scenario.code === 'SCN-05'"
                  class="details-content"
                >
                  <div class="details-grid">
                    <div>
                      <span>HFFRI</span>

                      <strong>
                        {{ formatPercentage(facility.details.hffri) }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.financialCoverage") }}
                      </span>

                      <strong>
                        {{ formatPercentage(facility.details.coverage_pct) }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.requiredBudget") }}
                      </span>

                      <strong>
                        {{ formatCurrency(facility.details.required_budget) }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.currentBudget") }}
                      </span>

                      <strong>
                        {{ formatCurrency(facility.details.current_budget) }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.financialGap") }}
                      </span>

                      <strong>
                        {{ formatCurrency(facility.details.budget_gap) }}
                      </strong>
                    </div>

                    <div>
                      <span>
                        {{ tr("details.salaryDelay") }}
                      </span>

                      <strong>
                        {{
                          translateSalaryDelay(
                            facility.details.salary_payment_delay
                          )
                        }}
                      </strong>
                    </div>
                  </div>

                  <div
                    v-if="facility.details.reduced_services"
                    class="details-note"
                  >
                    <span>
                      {{ tr("details.affectedServices") }}
                    </span>

                    <p>
                      {{ facility.details.reduced_services }}
                    </p>
                  </div>
                </div>
              </details>
            </article>
          </div>

          <p v-else class="empty-facilities">
            {{ tr("states.noFacilities") }}
          </p>
        </div>

        <!-- Decision -->
        <div class="decision-box">
          <div class="decision-icon">!</div>

          <div>
            <h3>
              {{ tr("immediateDecision") }}
            </h3>

            <p>
              {{ getScenarioDecision(scenario) }}
            </p>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import api from "@/plugins/api";

const { t, locale } = useI18n();

const loading = ref(false);
const initialLoading = ref(true);
const refreshing = ref(false);
const errorMessage = ref("");
const scenarios = ref([]);

const summary = ref({
  activated: 0,
  affected: 0,
  urgent: 0,
  max_timeframe: "-",
});

const tr = (key, params = {}) => {
  return t(`decisionScenarios.${key}`, params);
};

const isArabic = computed(() => {
  return locale.value === "ar";
});

const pageDirection = computed(() => {
  return isArabic.value ? "rtl" : "ltr";
});

const currentLanguage = computed(() => {
  return isArabic.value ? "ar" : "en";
});

const summaryItems = computed(() => [
  {
    key: "activated",
    value: summary.value.activated,
    label: tr("summary.activated"),
    className: "",
  },
  {
    key: "affected",
    value: summary.value.affected,
    label: tr("summary.affected"),
    className: "danger",
  },
  {
    key: "urgent",
    value: summary.value.urgent,
    label: tr("summary.urgent"),
    className: "warning",
  },
  {
    key: "timeframe",
    value: summary.value.max_timeframe || "-",
    label: tr("summary.maxTimeframe"),
    className: "blue",
  },
]);

const fetchDecisionScenarios = async () => {
  if (loading.value) {
    return;
  }

  loading.value = true;
  errorMessage.value = "";

  if (scenarios.value.length > 0) {
    refreshing.value = true;
  }

  try {
    const response = await api.get("/decision-scenarios", {
      params: {
        lang: currentLanguage.value,
      },
    });

    const payload = response.data?.data;

    scenarios.value = Array.isArray(payload?.scenarios)
      ? payload.scenarios
      : [];

    summary.value = {
      activated: Number(payload?.summary?.activated ?? 0),
      affected: Number(payload?.summary?.affected ?? 0),
      urgent: Number(payload?.summary?.urgent ?? 0),
      max_timeframe: payload?.summary?.max_timeframe || "-",
    };
  } catch (error) {
    console.error("Decision scenarios loading error:", error);

    errorMessage.value =
      error.response?.data?.message || tr("states.connectionError");
  } finally {
    loading.value = false;
    initialLoading.value = false;
    refreshing.value = false;
  }
};

const getScenarioTranslation = (scenario, field) => {
  const key = `scenarios.${scenario.code}.${field}`;
  const fullKey = `decisionScenarios.${key}`;
  const translatedValue = t(fullKey);

  if (translatedValue === fullKey || translatedValue === key) {
    return scenario[field] || "-";
  }

  return translatedValue;
};

const getScenarioTitle = (scenario) => {
  return getScenarioTranslation(scenario, "title");
};

const getScenarioTrigger = (scenario) => {
  return getScenarioTranslation(scenario, "trigger");
};

const getScenarioDecision = (scenario) => {
  return getScenarioTranslation(scenario, "decision");
};

const getSeverityLabel = (scenario) => {
  const values = {
    danger: tr("severity.critical"),
    warning: tr("severity.high"),
    medium: tr("severity.medium"),
    normal: tr("severity.low"),
  };

  return values[scenario.severity] || scenario.level || "-";
};

const getPriorityLabel = (priority) => {
  const priorities = {
    1: tr("priorities.critical"),
    2: tr("priorities.high"),
    3: tr("priorities.medium"),
    4: tr("priorities.low"),
  };

  return priorities[Number(priority)] || tr("priorities.undefined");
};

const getPriorityClass = (priority) => {
  const classes = {
    1: "priority-danger",
    2: "priority-warning",
    3: "priority-medium",
    4: "priority-normal",
  };

  return classes[Number(priority)] || "priority-normal";
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const number = Number(value);

  if (Number.isNaN(number)) {
    return value;
  }

  return new Intl.NumberFormat(isArabic.value ? "ar-YE" : "en-US", {
    maximumFractionDigits: 1,
  }).format(number);
};

const formatPercentage = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return `${formatNumber(value)}${isArabic.value ? "٪" : "%"}`;
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const number = Number(value);

  if (Number.isNaN(number)) {
    return value;
  }

  const formatted = new Intl.NumberFormat(isArabic.value ? "ar-YE" : "en-US", {
    maximumFractionDigits: 0,
  }).format(number);

  return `${formatted} ${tr("values.yer")}`;
};

const formatHours = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return tr("values.hours", {
    count: formatNumber(value),
  });
};

const translateColdChain = (value) => {
  const values = {
    available: tr("coldChain.available"),
    partially: tr("coldChain.partially"),
    not_available: tr("coldChain.notAvailable"),
  };

  return values[value] || value || "-";
};

const translateSalaryDelay = (value) => {
  const values = {
    no_delay: tr("salaryDelay.noDelay"),
    less_than_1_month: tr("salaryDelay.lessThanMonth"),
    "1_2_months": tr("salaryDelay.oneToTwoMonths"),
    "3_6_months": tr("salaryDelay.threeToSixMonths"),
    more_than_6_months: tr("salaryDelay.moreThanSixMonths"),
  };

  return values[value] || value || "-";
};

watch(locale, () => {
  errorMessage.value = "";
});

onMounted(() => {
  fetchDecisionScenarios();
});
</script>

<style scoped>
.scenarios-page {
  min-height: 100vh;
  padding: 28px;
  overflow-x: hidden;
  color: #071126;
  background: #f4f6f9;
  font-family: "Inter", "Cairo", sans-serif;
}

.scenarios-page[dir="rtl"] {
  text-align: right;
  font-family: "Cairo", "Inter", sans-serif;
}

.scenarios-page[dir="ltr"] {
  text-align: left;
  font-family: "Inter", "Cairo", sans-serif;
}

/* Header */

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.page-header-content {
  min-width: 0;
}

.page-header h1 {
  margin: 0 0 8px;
  color: #061226;
  font-size: 28px;
  font-weight: 900;
  line-height: 1.5;
}

.page-header p {
  max-width: 1100px;
  margin: 0 0 10px;
  color: #52657d;
  font-size: 14px;
  line-height: 1.9;
}

.reference-badge {
  display: inline-block;
  padding: 6px 14px;
  color: #64748b;
  font-size: 12px;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 999px;
}

/* Refresh button */

.refresh-button {
  display: inline-flex;
  min-width: 160px;
  min-height: 44px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 10px 16px;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  background: #143a63;
  border: 0;
  border-radius: 10px;
  transition: background 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
}

.refresh-button:hover:not(:disabled) {
  background: #0d2d4f;
  transform: translateY(-1px);
}

.refresh-button:disabled {
  cursor: wait;
  opacity: 0.75;
}

.refresh-icon {
  font-size: 21px;
  line-height: 1;
}

.button-spinner {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: scenario-spin 0.7s linear infinite;
}

/* Summary */

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 22px;
}

.summary-card {
  min-width: 0;
  padding: 18px;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-inline-start: 5px solid #143a63;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(15, 35, 64, 0.05);
}

.summary-card strong {
  display: block;
  margin-bottom: 4px;
  color: #143a63;
  font-size: 28px;
  font-weight: 900;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.summary-card span {
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.summary-card.danger {
  border-inline-start-color: #dc2626;
}

.summary-card.danger strong {
  color: #dc2626;
}

.summary-card.warning {
  border-inline-start-color: #d97706;
}

.summary-card.warning strong {
  color: #d97706;
}

.summary-card.blue {
  border-inline-start-color: #2563eb;
}

.summary-card.blue strong {
  color: #2563eb;
}

/* States */

.state-box {
  display: flex;
  min-height: 220px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  padding: 28px;
  text-align: center;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 14px;
}

.loading-spinner {
  width: 44px;
  height: 44px;
  border: 4px solid #dbe3ee;
  border-top-color: #143a63;
  border-radius: 50%;
  animation: scenario-spin 0.8s linear infinite;
}

@keyframes scenario-spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-box strong,
.state-box > strong {
  color: #061226;
  font-size: 17px;
}

.loading-box span,
.state-box p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.8;
}

.state-icon {
  display: grid;
  width: 42px;
  min-width: 42px;
  height: 42px;
  place-items: center;
  color: #fff;
  font-size: 20px;
  font-weight: 900;
  background: #b91c1c;
  border-radius: 50%;
}

.error-box {
  background: #fff7f7;
  border-color: #fecaca;
}

.retry-button {
  padding: 9px 18px;
  color: #fff;
  font-weight: 800;
  cursor: pointer;
  background: #b91c1c;
  border: 0;
  border-radius: 9px;
}

.empty-box .state-icon {
  background: #64748b;
}

/* Scenario list */

.scenarios-list {
  display: grid;
  gap: 16px;
  transition: opacity 0.2s ease;
}

.scenarios-list.is-refreshing {
  pointer-events: none;
  opacity: 0.68;
}

.scenario-card {
  min-width: 0;
  padding: 22px;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(15, 35, 64, 0.05);
}

.scenario-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px solid #e2e8f0;
}

.scenario-title-group {
  min-width: 0;
}

.scenario-number {
  margin-bottom: 5px;
  color: #64748b;
  font-size: 13px;
  font-weight: 800;
}

.scenario-head h2 {
  margin: 0;
  color: #061226;
  font-size: 20px;
  font-weight: 900;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.scenario-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-top: 8px;
  color: #64748b;
  font-size: 12px;
}

.scenario-meta strong {
  color: #143a63;
}

.severity {
  flex-shrink: 0;
  padding: 7px 13px;
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
  border-radius: 999px;
}

.severity.danger {
  color: #991b1b;
  background: #fee2e2;
}

.severity.warning {
  color: #92400e;
  background: #fef3c7;
}

.severity.medium {
  color: #1e40af;
  background: #dbeafe;
}

.severity.normal {
  color: #166534;
  background: #dcfce7;
}

/* Trigger */

.trigger-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 16px;
  margin-bottom: 18px;
  background: #f8fafc;
  border: 1px solid #dbe3ee;
  border-radius: 12px;
}

.trigger-box span {
  flex-shrink: 0;
  color: #64748b;
  font-size: 13px;
}

.trigger-box strong {
  min-width: 0;
  color: #143a63;
  font-size: 15px;
  font-weight: 900;
  line-height: 1.7;
  overflow-wrap: anywhere;
}

/* Facilities */

.facilities-box {
  margin-bottom: 18px;
}

.facilities-box > h3,
.decision-box h3 {
  margin: 0 0 10px;
  color: #061226;
  font-size: 14px;
  font-weight: 900;
}

.facilities-count {
  color: #64748b;
  font-size: 12px;
}

.facilities-list {
  display: grid;
  gap: 12px;
}

.facility-card {
  min-width: 0;
  padding: 15px;
  background: #fbfdff;
  border: 1px solid #dbe3ee;
  border-inline-start: 4px solid #64748b;
  border-radius: 12px;
}

.facility-card.urgent {
  background: #fffafa;
  border-inline-start-color: #dc2626;
}

.facility-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.facility-title {
  min-width: 0;
}

.facility-head h4 {
  margin: 0 0 5px;
  color: #061226;
  font-size: 15px;
  font-weight: 900;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.facility-head p {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  color: #64748b;
  font-size: 11px;
}

.facility-head p span:not(:last-child)::after {
  margin-inline-start: 6px;
  content: "•";
}

.facility-priority {
  flex-shrink: 0;
  padding: 6px 10px;
  font-size: 10px;
  font-weight: 900;
  white-space: nowrap;
  border-radius: 999px;
}

.priority-danger {
  color: #991b1b;
  background: #fee2e2;
}

.priority-warning {
  color: #92400e;
  background: #fef3c7;
}

.priority-medium {
  color: #1e40af;
  background: #dbeafe;
}

.priority-normal {
  color: #166534;
  background: #dcfce7;
}

/* Facility info */

.facility-info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.facility-info {
  min-width: 0;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
}

.facility-info span {
  display: block;
  margin-bottom: 4px;
  color: #64748b;
  font-size: 10px;
}

.facility-info strong {
  display: block;
  color: #143a63;
  font-size: 12px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

/* Reasons */

.facility-reason,
.facility-phase-reason {
  padding: 11px 13px;
  margin-top: 10px;
  border-radius: 9px;
}

.facility-reason {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
}

.facility-phase-reason {
  background: #fff7ed;
  border: 1px solid #fed7aa;
}

.facility-reason span,
.facility-phase-reason span,
.details-note span {
  display: block;
  margin-bottom: 4px;
  color: #64748b;
  font-size: 10px;
  font-weight: 800;
}

.facility-reason p,
.facility-phase-reason p,
.details-note p {
  margin: 0;
  color: #334155;
  font-size: 12px;
  line-height: 1.8;
  overflow-wrap: anywhere;
}

/* Details */

.facility-details {
  margin-top: 12px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 9px;
}

.facility-details summary {
  padding: 11px 12px;
  color: #143a63;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
  user-select: none;
}

.facility-details[open] summary {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.details-content {
  padding: 12px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 9px;
}

.details-grid > div {
  min-width: 0;
  padding: 10px;
  background: #f8fafc;
  border-radius: 8px;
}

.details-grid span {
  display: block;
  margin-bottom: 4px;
  color: #64748b;
  font-size: 10px;
}

.details-grid strong {
  display: block;
  color: #143a63;
  font-size: 12px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.details-note {
  padding: 11px;
  margin-top: 10px;
  background: #f8fafc;
  border-radius: 8px;
}

/* Table */

.details-table-wrapper {
  width: 100%;
  margin-top: 10px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.details-table {
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
}

.details-table th,
.details-table td {
  padding: 9px;
  text-align: start;
  font-size: 11px;
  white-space: nowrap;
  border-bottom: 1px solid #e2e8f0;
}

.details-table th {
  color: #475569;
  background: #f8fafc;
}

/* Empty facility */

.empty-facilities {
  margin: 0;
  padding: 12px;
  color: #64748b;
  font-size: 13px;
  background: #f8fafc;
  border-radius: 10px;
}

/* Decision */

.decision-box {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 15px 16px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 12px;
}

.decision-icon {
  display: grid;
  width: 28px;
  min-width: 28px;
  height: 28px;
  place-items: center;
  color: #fff;
  font-weight: 900;
  background: #d97706;
  border-radius: 50%;
}

.decision-box p {
  margin: 0;
  color: #7c2d12;
  font-size: 13px;
  line-height: 1.9;
  overflow-wrap: anywhere;
}

/* Tablet */

@media (max-width: 1100px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .details-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Mobile */

@media (max-width: 768px) {
  .scenarios-page {
    padding: 16px 10px 28px;
  }

  .page-header {
    align-items: stretch;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 18px;
  }

  .page-header h1 {
    font-size: 22px;
    line-height: 1.55;
  }

  .page-header p {
    font-size: 13px;
    line-height: 1.8;
  }

  .refresh-button {
    width: 100%;
    min-height: 46px;
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 18px;
  }

  .summary-card {
    min-height: 110px;
    padding: 14px;
  }

  .summary-card strong {
    font-size: 24px;
  }

  .summary-card span {
    font-size: 11px;
  }

  .state-box {
    min-height: 190px;
    padding: 20px 14px;
  }

  .scenario-card {
    padding: 14px;
    border-radius: 14px;
  }

  .scenario-head {
    flex-direction: column;
    gap: 10px;
    padding-bottom: 12px;
  }

  .scenario-head h2 {
    font-size: 17px;
  }

  .scenario-meta {
    display: grid;
    grid-template-columns: 1fr;
    gap: 5px;
  }

  .severity {
    align-self: flex-start;
  }

  .trigger-box {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
    padding: 12px;
  }

  .trigger-box strong {
    width: 100%;
    font-size: 13px;
  }

  .facility-card {
    padding: 13px;
  }

  .facility-head {
    flex-direction: column;
  }

  .facility-priority {
    align-self: flex-start;
  }

  .facility-info-grid,
  .details-grid {
    grid-template-columns: 1fr;
  }

  .decision-box {
    padding: 13px;
  }

  .decision-box p {
    font-size: 12px;
  }
}

/* Small mobile */

@media (max-width: 420px) {
  .scenarios-page {
    padding-inline: 8px;
  }

  .page-header h1 {
    font-size: 20px;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .summary-card {
    min-height: auto;
  }

  .scenario-card {
    padding: 12px;
  }

  .trigger-box,
  .decision-box {
    padding: 11px;
  }

  .decision-box {
    flex-direction: column;
  }

  .facility-info {
    padding: 9px 10px;
  }
}
</style>
